/**
 * Inverse solvers — from a wanted take vector back to the solo anchors that
 * produce it. Both run the same multiplicative fixed point (take_i is monotone
 * in solo_i holding the others); they differ only in the contract they honour.
 *
 *   `solveSolosForTakes`   — STRICT. Calibrating against SERVED data: refuse
 *                            unless the landing is inside 0.5pp, because a bad
 *                            seed is worse than none. Shipped, frozen.
 *   `solveSolosBestEffort` — a share SLIDER is a different contract: the visitor
 *                            is stating an intent, so run the fixed point harder
 *                            and hand back whatever it reached PLUS the residual.
 *
 * Pure: imports only its own siblings in this directory.
 */

import { clampSolo, menuTakes, productMenuTakes, type CalculatorOffer } from './model';
import { type UnitProduct } from './product';

/**
 * Inverse of `menuTakes` (Phase 1 «pull served», AC-014): find solo anchors
 * whose nested-logit menu distribution reproduces the OBSERVED paid|pressed
 * takes. Observed in-menu takes are NOT solo anchors — a menu suppresses each
 * member below its solo rate — so served mixes seed the model through this
 * calibration, never by writing takes into `solos` directly.
 *
 * Multiplicative fixed-point with damping (take_i is monotone in solo_i
 * holding the others). Offers on the menu with NO observed take stay at the
 * clamp floor (their presence still shapes the substitution pattern).
 * Returns null when the targets are not representable: Σ takes ≥ 0.99 (the
 * no-buy option needs positive share) or no positive target — a typed
 * refusal, never a bad seed.
 */
export function solveSolosForTakes(
  menu: string[],
  offers: Record<string, CalculatorOffer>,
  targetTakes: Record<string, number>,
): Record<string, number> | null {
  const members = menu.filter(id => offers[id] && offers[id].price !== null);
  const calibrated = members.filter(id => (targetTakes[id] ?? 0) > 0);
  if (calibrated.length === 0) return null;
  const total = calibrated.reduce((sum, id) => sum + (targetTakes[id] ?? 0), 0);
  if (total >= 0.99) return null;
  const solos: Record<string, number> = {};
  members.forEach(id => {
    solos[id] = clampSolo(targetTakes[id] ?? 0.005);
  });
  for (let iteration = 0; iteration < 400; iteration += 1) {
    const takes = menuTakes(members, offers, solos);
    let worst = 0;
    calibrated.forEach(id => {
      const current = takes[id] ?? 0;
      const target = targetTakes[id]!;
      worst = Math.max(worst, Math.abs(current - target));
      if (current > 0) solos[id] = clampSolo(solos[id] * (target / current) ** 0.8);
    });
    if (worst < 1e-6) break;
  }
  const final = menuTakes(members, offers, solos);
  const residual = calibrated.reduce(
    (max, id) => Math.max(max, Math.abs((final[id] ?? 0) - targetTakes[id]!)),
    0,
  );
  return residual < 0.005 ? solos : null;
}


/** What a best-effort solve reached, and how far it stayed from the target. */
export interface BestEffortSolve {
  solos: Record<string, number>;
  /** Largest absolute gap between a calibrated target take and the landed one. */
  residual: number;
}

/**
 * Best-effort inverse over products. `solveSolosForTakes` refuses unless it lands
 * within 0.5pp, which is right for served data. A slider states an intent, so the
 * same fixed point runs longer (1200 iterations) with damping that decays
 * (0.8 → 0.5 → 0.3), from four seeds, and the closest landing wins.
 *
 * The first seed is WHERE WE ALREADY ARE: a slider move is a small perturbation,
 * and the fixed point converges far better from a near solution than from the raw
 * targets (which diverges when two products sit in one nest at near-identical
 * prices). The caller decides what to do with the residual — the displayed mix is
 * always re-read from the model, so an approximate landing is still honest.
 *
 * Returns null ONLY when the request is meaningless: no positive target, or
 * Σ targets ≥ 0.99 (the no-buy option needs positive share). It never throws.
 */
export function solveSolosBestEffort(
  menu: string[],
  products: Record<string, UnitProduct>,
  targetTakes: Record<string, number>,
  startSolos?: Record<string, number>,
): BestEffortSolve | null {
  const members = menu.filter(id => products[id]);
  const calibrated = members.filter(id => (targetTakes[id] ?? 0) > 0);
  if (calibrated.length === 0) return null;
  if (calibrated.reduce((sum, id) => sum + (targetTakes[id] ?? 0), 0) >= 0.99) return null;

  let best: BestEffortSolve | null = null;
  const seeds: Array<Record<string, number> | number> = [startSolos ?? 1, 1, 0.5, 2];
  for (const seed of seeds) {
    const solos: Record<string, number> = {};
    members.forEach(id => {
      solos[id] =
        typeof seed === 'object'
          ? clampSolo(seed[id] ?? 0.005)
          : clampSolo((targetTakes[id] ?? 0.005) * seed);
    });
    for (let iteration = 0; iteration < 1200; iteration += 1) {
      const takes = productMenuTakes(members, products, solos);
      const damping = iteration < 400 ? 0.8 : iteration < 800 ? 0.5 : 0.3;
      let worst = 0;
      calibrated.forEach(id => {
        const current = takes[id] ?? 0;
        const target = targetTakes[id]!;
        worst = Math.max(worst, Math.abs(current - target));
        if (current > 0) solos[id] = clampSolo(solos[id] * (target / current) ** damping);
      });
      if (worst < 1e-6) break;
    }
    const final = productMenuTakes(members, products, solos);
    const residual = calibrated.reduce(
      (max, id) => Math.max(max, Math.abs((final[id] ?? 0) - targetTakes[id]!)),
      0,
    );
    if (!best || residual < best.residual) best = { solos, residual };
    if (residual < 0.001) break;
  }
  return best;
}
